import { useEffect, useState, useCallback } from "react";
import axiosInstance from "@/api/AxiosInstance";
import getUserCart from "@/api/getUserCart";
import type { Cart } from "../types";

const useUserCart = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUserCart();

      if (res?.unauthorized) {
        setIsAuthenticated(false);
        setCart(null);
        return;
      }

      setIsAuthenticated(true);
      setCart(res?.[0] ?? null);
    } catch (err: any) {
      console.error("Failed to fetch cart:", err);
      setError(err?.message || "Failed to fetch cart");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const removeFromCart = useCallback(
    async (itemId: number) => {
      if (loadingId === itemId || !isAuthenticated) return;
      setLoadingId(itemId);

      setCart((prev) => {
        if (!prev) return prev;
        const updatedItems = prev.items.filter((item) => item.id !== itemId);
        return { ...prev, items: updatedItems };
      });

      try {
        await axiosInstance.delete(`/api/cart-items/${itemId}/`);
      } catch (err: any) {
        console.error("Failed to remove item:", err);
        setError(err?.message || "Failed to remove item");
        await fetchCart();
      } finally {
        setLoadingId(null);
      }
    },
    [loadingId, fetchCart, isAuthenticated]
  );

  const updateQuantity = useCallback(
    async (itemId: number, quantity: number) => {
      if (loadingId === itemId || !isAuthenticated) return;
      setLoadingId(itemId);

      try {
        if (quantity <= 0) {
          await removeFromCart(itemId);
          return;
        }

        setCart((prev) => {
          if (!prev) return prev;
          const updatedItems = prev.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          );
          return { ...prev, items: updatedItems };
        });

        const res = await axiosInstance.patch(`/api/cart-items/${itemId}/`, {
          quantity,
        });

        setCart((prev) => {
          if (!prev) return prev;
          const updatedItems = prev.items.map((item) =>
            item.id === itemId ? { ...item, quantity: res.data.quantity } : item
          );
          return { ...prev, items: updatedItems };
        });
      } catch (err: any) {
        console.error("Failed to update quantity:", err);
        setError(err?.message || "Failed to update quantity");
        await fetchCart();
      } finally {
        setLoadingId(null);
      }
    },
    [loadingId, removeFromCart, fetchCart, isAuthenticated]
  );

  return {
    cart,
    loading,
    error,
    loadingId,
    isAuthenticated,
    refreshCart: fetchCart,
    updateQuantity,
    removeFromCart,
  };
};

export default useUserCart;
